import { useMemo } from "react"
import { Chart } from "../../../../Common/Lazy/Chart"
import { APIJobsPerStatusModel } from "../../../../../Models/API/APIJobsPerStatusModel"
import type { ChartConfiguration } from "chart.js"

enum Color {
    violet = "#8F0DA9",
    grey = "#D8D8D8",
    lightGrey = "#EBEBEC",
    red = "#931100",
    black = "#000",
    pink = "#ea3f80",
    lightpink = "#ffd6e5"
}

// in case data is not loaded, this will help create a chart placeholder

const chartOptions = {
    animation: {
        duration: 0
    },
    scales: {
        xAxes: [
            {
                stacked: true,
                display: false,
                ticks: {
                    beginAtZero: true
                }
            }
        ],
        yAxes: [
            {
                display: false,
                ticks: {
                    beginAtZero: true
                }
            }
        ]
    },
    responsive: true,
    aspectRatio: 6,
    maintainAspectRatio: true,
    legend: {
        display: false
    },
    tooltips: {
        enabled: false
    },
    plugins: {
        datalabels: {
            labels: {
                title: null
            }
        }
    }
}

const defaultConfig: ChartConfiguration = {
    type: "horizontalBar",
    data: {
        datasets: Object.values(Color).map(c => ({
            stack: "stack 0",
            data: [0],
            backgroundColor: [c],
            barThickness: 60,
            maxBarThickness: 60,
            hoverBackgroundColor: c
        }))
    },
    options: chartOptions
}

interface Props {
    data: APIJobsPerStatusModel
    total: number
}
export const JobsProgressChart = ({ data, total }: Props) => {
    const progressConfig: ChartConfiguration | undefined = useMemo(() => {
        if (!data) return undefined
        const config: ChartConfiguration = {
            type: "horizontalBar",
            data: {
                datasets: [
                    {
                        stack: "stack 0",
                        data: [data.COMPLETED / total],
                        backgroundColor: [Color.violet],
                        barThickness: 60,
                        maxBarThickness: 60,
                        hoverBackgroundColor: Color.violet
                    },
                    {
                        stack: "stack 0",
                        data: [data.IN_PROGRESS / total],
                        backgroundColor: [Color.grey],
                        barThickness: 60,
                        maxBarThickness: 60,
                        hoverBackgroundColor: Color.grey
                    },
                    {
                        stack: "stack 0",
                        data: [data.SCHEDULED / total],
                        backgroundColor: [Color.lightGrey],
                        barThickness: 60,
                        maxBarThickness: 60,
                        hoverBackgroundColor: Color.lightGrey
                    },
                    {
                        stack: "stack 0",
                        data: [data.PENDING / total],
                        backgroundColor: [Color.lightpink],
                        barThickness: 60,
                        maxBarThickness: 60,
                        hoverBackgroundColor: Color.lightpink
                    },
                    {
                        stack: "stack 0",
                        data: [(data.FAILED + data.EXPIRED) / total],
                        backgroundColor: [Color.red],
                        barThickness: 60,
                        maxBarThickness: 60,
                        hoverBackgroundColor: Color.red
                    },
                    {
                        stack: "stack 0",
                        data: [data.CANCELED / total],
                        backgroundColor: [Color.black],
                        barThickness: 60,
                        maxBarThickness: 60,
                        hoverBackgroundColor: Color.black
                    },
                    {
                        stack: "stack 0",
                        data: [data.SUSPENDED / total],
                        backgroundColor: [Color.pink],
                        barThickness: 60,
                        maxBarThickness: 60,
                        hoverBackgroundColor: Color.pink
                    }
                ]
            },
            options: chartOptions
        }
        return config
    }, [data, total])

    return <Chart config={progressConfig ?? defaultConfig} />
}
